import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ProgressColourPalette } from "@app/directives/_misc/v-progress-bar/v-progress-bar.component";
import { VideoStatus } from "@app/main/components/videos/enums/VideoStatus";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { VideoEntryUtils } from "@app/main/components/videos/utils/video-entry-utils";
import { Subject, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import './video-download-progress.component.scss';

export enum RequestStep {
    Requested = 1,
    Retrieving = 2,
    Downloading = 3,
    Complete = 4,
    Faceblurring = 5,
    Error = 6,
}

@Component({
    selector: 'video-download-progress',
    template: require('./video-download-progress.component.html')
})
export class VideoDownloadProgressComponent {
    @Input() videoEntry: IVideoEntry;
    @Input() showSteps: boolean;
    @Output() statusChange: EventEmitter<RequestStep> = new EventEmitter<RequestStep>();

    palette: ProgressColourPalette = ProgressColourPalette.GreenOnly;
    percentage: number = 0;
    currentStep: RequestStep = RequestStep.Requested;

    VideoStatus = VideoStatus;
    RequestStep = RequestStep

    private latestOfflineProgressRetrieved: boolean = false;
    private downloadCompleted: boolean;
    private endTimer$: Subject<void> = new Subject<void>();

    constructor(
    ) { }

    ngOnInit(): void {
        this.loadProgress();
        timer(0, 10_000)
            .pipe(takeUntil(this.endTimer$))
            .subscribe(() => this.loadProgress());
    }

    ngOnDestroy(): void {
        this.endTimer$.next();
    }

    //--

    private loadProgress(): void {

        if (this.downloadCompleted) {
            return;
        }

        this.currentStep = this.currentStep || RequestStep.Requested;

        if (VideoEntryUtils.isDownloadFailed(this.videoEntry)) {
        // Video download is completed but there are no videos to view
            this.handleDownloadFailed();
            return;
        }

        if (VideoEntryUtils.isDownloaded(this.videoEntry)) {
        // Video download is successfully completed
            this.handleDownloadSuccessful();
            return;
        }

        if (VideoEntryUtils.isFaceblurring(this.videoEntry)) {
            this.handleFaceblurring();
            return;
        }

        if (this.videoEntry.Status == VideoStatus.Uploaded) {
        // Video is uploaded but not yet received on platform
            this.handleVideoUploaded();
            return;
        }


        if (!this.videoEntry.vehicle?.onlineStatus?.online && this.latestOfflineProgressRetrieved) {
            // Camera is offline
            return;
        }

        if (!!this.videoEntry.ExternalId) {
            this.percentage = this.percentage || 0;
        }


    }

    private handleDownloadFailed(): void {
        this.percentage = 0;
        this.currentStep = RequestStep.Error;
        this.statusChange.emit(this.currentStep);
        this.endTimer$.next();
    }

    private handleDownloadSuccessful(): void {
        this.percentage = 100;
        this.currentStep = RequestStep.Complete;

        if (!this.downloadCompleted) {
            this.statusChange.emit(this.currentStep);
        }

        this.downloadCompleted = true;

        this.endTimer$.next();
    }

    private handleVideoUploaded(): void {
        this.percentage = 75;
        this.currentStep = RequestStep.Downloading;
        this.statusChange.emit(this.currentStep);
    }

    private handleFaceblurring(): void {
        this.percentage = 90;
        this.currentStep = RequestStep.Faceblurring;
        this.statusChange.emit(this.currentStep);
    }
}
